/**
 * RegionMapScene — the calm "where shall we wander today?" map. Town -> *RegionMap*
 * -> Route. A short, friendly graph of biome destinations the player taps to choose
 * today's expedition. No pressure, no locked-out grind walls: a handful of open
 * biomes, each a soft card with its name + a one-line invitation, all reachable.
 *
 * The map itself is rendered as a calm placeholder (soft-colored ground + gentle
 * paths drawn with Graphics — no downloaded tilesets), so the art is swappable later
 * without touching the selection logic. The biome + region definitions live here as
 * static, renderer-adjacent config; the *encounter rules* stay in core/encounter,
 * which the Route scene drives via the session seed/date. The renderer owns no rules.
 *
 * Responsive + touch-first: the destination nodes are laid out across the safe rect
 * and reflow on resize; every node is a generous tap target, and a calm "back to
 * town" control is always reachable.
 */
import Phaser from 'phaser';
import { COLORS, CSS, FONT, RADIUS, SPACING } from './ui/theme.js';
import { makeClickable } from './ui/clickable.js';
import { Button } from './ui/Button.js';
import { ScrollList } from './ui/ScrollList.js';
import { FocusRing } from './ui/FocusRing.js';
import { moveGrid, type GridDir } from './ui/gridNav.js';
import { domTextFieldFocused } from './ui/keyboardGate.js';
import { Layout } from './layout/index.js';
import { fadeIn, goTo, isReducedMotion } from './flow.js';
import { peekSession } from './session.js';
import { REGION, biomeById, type BiomeDef, type RegionNodeDef } from './world/biomes.js';
import { GYM_LEADERS } from '@/data/quests';

/** One tappable destination on the map (a region node + its rendered card). */
interface MapNode {
  readonly node: RegionNodeDef;
  readonly biome: BiomeDef;
  readonly container: Phaser.GameObjects.Container;
  readonly card: Phaser.GameObjects.Graphics;
  readonly width: number;
  readonly height: number;
  /** The gymId hosted in this biome, if a Badge Circuit leader lives here. */
  readonly gymId?: string;
}

/** The Badge Circuit leader who calls this biome home, if any (soft, never a gate). */
function leaderForBiome(biomeId: string): (typeof GYM_LEADERS)[number] | undefined {
  return GYM_LEADERS.find((l) => l.biomeId === biomeId);
}

export class RegionMapScene extends Phaser.Scene {
  private layout!: Layout;

  private heading?: Phaser.GameObjects.Text;
  private subheading?: Phaser.GameObjects.Text;
  private ground?: Phaser.GameObjects.Graphics;
  private paths?: Phaser.GameObjects.Graphics;
  private nodes: MapNode[] = [];
  private homeButton?: Button;
  /**
   * The League Summit affordance — a soft invitation, NEVER a wall. It simply doesn't
   * exist until all 8 badges are earned (like an offerable quest); its absence blocks
   * nothing (every biome + the finale are reachable without it). Tapping → the League.
   */
  private summitButton?: Button;
  private summitReady = false;
  /**
   * On a tall narrow phone the 8 destination cards stack taller than the viewport, so
   * they go in a touch-drag ScrollList — otherwise the lower biomes fall below the fold
   * (and under the home button) with no way to reach them. On a roomy screen the grid
   * fits and is placed directly (with the decorative paths); the list stays unused.
   */
  private scroll?: ScrollList;
  private busy = false;

  // ── keyboard grid nav ──────────────────────────────────────────────────────
  /** The keyboard cursor index over `nodes`; -1 until the player first uses a key. */
  private cursor = -1;
  /** Columns the current layout used (for the row/col cursor math). */
  private gridCols = 1;
  /** Live list viewport height when the grid is scrolling (for keep-cursor-visible). */
  private scrollViewH = 0;
  private focusRing?: FocusRing;
  private keyHandler?: (e: KeyboardEvent) => void;

  constructor() {
    super('RegionMap');
  }

  create(): void {
    // ── CRITICAL: Phaser REUSES this scene instance, so create() re-runs on every
    // RE-ENTRY (Town → RegionMap → Route → … → RegionMap). Instance fields survive
    // between visits: `busy` was set true in setOut()/goHome() and NEVER reset (so the
    // map SOFT-LOCKED after one expedition), and buildNodes() PUSHED onto a stale
    // `this.nodes` (so the cards DUPLICATED and stacked 8→16→24, reading as scattered
    // dead cards). resetState() drops/destroys all of that so re-entry starts clean.
    this.resetState();

    this.cameras.main.setBackgroundColor(COLORS.bg);
    this.layout = Layout.attach(this);
    const reduced = isReducedMotion();

    // Calm placeholder map backdrop (drawn, never downloaded — swappable later).
    this.ground = this.add.graphics();
    this.paths = this.add.graphics();

    const session = peekSession(this);
    const name = session?.trainerName ?? 'friend';

    this.heading = this.add
      .text(0, 0, 'Where shall we wander today?', {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.lg}px`,
        color: CSS.text,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.subheading = this.add
      .text(0, 0, `Take your time, ${name}. Every path is gentle.`, {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.sm}px`,
        color: CSS.textMuted,
      })
      .setOrigin(0.5);

    this.buildNodes(reduced);

    this.homeButton = new Button(this, 0, 0, {
      label: 'Back to town',
      variant: 'ghost',
      width: 200,
      reducedMotion: reduced,
      onClick: () => this.goHome(),
    });

    // Soft visibility: the Summit appears only once all 8 badges are in hand (never a
    // gate — its absence blocks nothing). A warm, tappable invitation to the League.
    this.summitReady = (session?.questState.badges.length ?? 0) >= 8;
    if (this.summitReady) {
      this.summitButton = new Button(this, 0, 0, {
        label: '⛰  The Summit — League',
        variant: 'warm',
        width: 260,
        reducedMotion: reduced,
        onClick: () => this.enterLeague(),
      });
    }

    this.focusRing = new FocusRing(this, { depth: 5 });

    this.relayout();
    this.layout.on('change', () => this.relayout());
    this.setupKeyboard();
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    fadeIn(this);
  }

  /**
   * Full keyboard nav: arrows move a highlight across the biome cards, Enter departs to
   * that route, Esc heads back to town. Ignored while a DOM text field is focused (there
   * is none here, but the guard keeps the convention). Cleaned up on SHUTDOWN.
   */
  private setupKeyboard(): void {
    this.keyHandler = (e: KeyboardEvent): void => {
      if (this.busy) return;
      if (typeof document !== 'undefined' && domTextFieldFocused(document.activeElement)) return;
      const dirs: Record<string, GridDir> = {
        ArrowUp: 'up',
        ArrowDown: 'down',
        ArrowLeft: 'left',
        ArrowRight: 'right',
        w: 'up',
        s: 'down',
        a: 'left',
        d: 'right',
      };
      const dir = dirs[e.key];
      if (dir) {
        e.preventDefault();
        const start =
          this.cursor < 0 ? 0 : moveGrid(this.cursor, dir, this.nodes.length, this.gridCols);
        this.setCursor(start);
      } else if (e.key === 'Enter' || e.key === ' ') {
        if (this.cursor >= 0 && this.nodes[this.cursor]) {
          e.preventDefault();
          void this.setOut(this.nodes[this.cursor]!.node);
        }
      } else if (e.key === 'g' || e.key === 'G') {
        // Challenge the gym on the focused card (if this biome hosts a leader).
        const gymId = this.cursor >= 0 ? this.nodes[this.cursor]?.gymId : undefined;
        if (gymId) {
          e.preventDefault();
          this.enterGym(gymId);
        }
      } else if ((e.key === 'l' || e.key === 'L') && this.summitReady) {
        // Enter the League Summit (only once it exists — soft visibility, never a wall).
        e.preventDefault();
        this.enterLeague();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        this.goHome();
      }
    };
    if (typeof window !== 'undefined') window.addEventListener('keydown', this.keyHandler);
  }

  /** Seat the keyboard cursor on a card: scroll it into view + draw the focus ring. */
  private setCursor(index: number): void {
    if (this.nodes.length === 0) return;
    this.cursor = Math.max(0, Math.min(this.nodes.length - 1, index));
    const n = this.nodes[this.cursor];
    if (!n) return;
    // Keep the focused card inside a scrolling (phone) viewport before drawing the ring.
    if (this.scroll && this.scrollViewH > 0) {
      const top = n.container.y - n.height / 2;
      const bottom = n.container.y + n.height / 2;
      const off = this.scroll.scrollOffset;
      const pad = SPACING.sm;
      if (top < off) this.scroll.scrollTo(top - pad);
      else if (bottom > off + this.scrollViewH)
        this.scroll.scrollTo(bottom - this.scrollViewH + pad);
    }
    const m = n.container.getWorldTransformMatrix();
    this.focusRing?.show(m.tx, m.ty, n.width, n.height);
    this.focusRing?.bringToTop();
  }

  /**
   * Drop every reference a prior visit's create() populated, so re-entry starts clean.
   * Display objects were torn down by the previous SHUTDOWN (teardown()); this clears the
   * arrays/flags so nothing stacks and the soft-lock guard releases. Safe on first entry
   * (everything is already empty / false).
   */
  private resetState(): void {
    this.nodes = [];
    this.heading = undefined;
    this.subheading = undefined;
    this.ground = undefined;
    this.paths = undefined;
    this.homeButton = undefined;
    this.summitButton = undefined;
    this.summitReady = false;
    this.scroll = undefined;
    this.focusRing = undefined;
    this.keyHandler = undefined;
    this.cursor = -1;
    this.gridCols = 1;
    this.scrollViewH = 0;
    this.busy = false;
  }

  /**
   * SHUTDOWN handler — Phaser reuses this scene instance, so leave NOTHING behind that a
   * re-entry would trip over: destroy every node container (cascades to its card/labels)
   * and the backdrop/heading/home button, then drop the references. Without this, the
   * cards would stack on every return and "Set out" would silently no-op.
   */
  private teardown(): void {
    this.tweens.killAll();
    if (this.keyHandler && typeof window !== 'undefined') {
      window.removeEventListener('keydown', this.keyHandler);
    }
    this.keyHandler = undefined;
    this.focusRing?.destroy();
    this.focusRing = undefined;
    for (const n of this.nodes) n.container.destroy();
    this.nodes = [];
    this.scroll?.destroy();
    this.ground?.destroy();
    this.paths?.destroy();
    this.heading?.destroy();
    this.subheading?.destroy();
    this.homeButton?.destroy();
    this.summitButton?.destroy();
    this.scroll = undefined;
    this.ground = undefined;
    this.paths = undefined;
    this.heading = undefined;
    this.subheading = undefined;
    this.homeButton = undefined;
    this.summitButton = undefined;
  }

  // ── destination nodes ──────────────────────────────────────────────────────

  private buildNodes(reduced: boolean): void {
    const w = 188;
    const blurbWrap = w - SPACING.lg;

    // MEASURE-TO-CONTENT: the cards share one uniform height in the grid, so size that
    // height to the TALLEST node's title+kind+blurb stack. A long invitation that wraps
    // to an extra line on the narrow 164px card would otherwise overflow the fixed 116px
    // card and spill onto the border / the card below it.
    let maxBlurbH = 0;
    for (const node of REGION.nodes) {
      const biome = biomeById(node.biomeId);
      if (!biome) continue;
      const probe = this.add.text(0, 0, biome.invitation, {
        fontFamily: FONT.family,
        fontSize: `${FONT.size.xs}px`,
        wordWrap: { width: blurbWrap },
      });
      maxBlurbH = Math.max(maxBlurbH, probe.height);
      probe.destroy();
    }
    // Stack: pad + title(md) + xs + kind(xs) + sm + blurb + pad. Use a clear floor so
    // the layout still looks generous when every invitation is short.
    // When ANY biome hosts a gym, reserve one extra line at the bottom of EVERY card so
    // the gym marker row (badge + leader name) never rides onto the blurb — cards keep a
    // uniform height in the grid, so the reservation must be uniform too.
    const hasAnyGym = GYM_LEADERS.length > 0;
    const gymLineH = hasAnyGym ? SPACING.sm + FONT.size.xs : 0;
    const stackH =
      SPACING.md +
      FONT.size.md +
      SPACING.xs +
      FONT.size.xs +
      SPACING.sm +
      maxBlurbH +
      gymLineH +
      SPACING.md;
    const h = Math.max(116, Math.ceil(stackH));

    for (const node of REGION.nodes) {
      const biome = biomeById(node.biomeId);
      if (!biome) continue;

      const container = this.add.container(0, 0);
      const card = this.add.graphics();

      const title = this.add
        .text(0, -h / 2 + SPACING.md, node.name, {
          fontFamily: FONT.family,
          fontSize: `${FONT.size.md}px`,
          color: CSS.text,
          fontStyle: 'bold',
        })
        .setOrigin(0.5, 0);

      // The card title already shows the place name (node.name === biome.name), so this
      // accent-colored sub-line shows the biome KIND (a one-word mood hint) rather than
      // repeating the name verbatim.
      const kind = this.add
        .text(0, -h / 2 + SPACING.md + FONT.size.md + SPACING.xs, titleCase(biome.kind), {
          fontFamily: FONT.family,
          fontSize: `${FONT.size.xs}px`,
          color: hexCss(biome.accent),
          fontStyle: 'bold',
        })
        .setOrigin(0.5, 0);

      // Seat the blurb directly UNDER the kind line (measured), not at a fixed offset,
      // so it can never ride up onto the kind text or down through the card border.
      const blurbY = -h / 2 + SPACING.md + FONT.size.md + SPACING.xs + FONT.size.xs + SPACING.sm;
      const blurb = this.add
        .text(0, blurbY, biome.invitation, {
          fontFamily: FONT.family,
          fontSize: `${FONT.size.xs}px`,
          color: CSS.textMuted,
          align: 'center',
          wordWrap: { width: blurbWrap },
        })
        .setOrigin(0.5, 0);

      container.add([card, title, kind, blurb]);

      // Gentle gym marker: a small badge-shaped icon + the leader's name, seated in the
      // reserved bottom line. It's its OWN tappable target (a "Challenge the gym" affordance
      // for touch) — under Phaser's default top-only hit-testing it wins the tap over the
      // card beneath it, so tapping the marker enters the gym while tapping the card body
      // still departs to the route. No unlock gate: every gym is always reachable.
      const leader = leaderForBiome(node.biomeId);
      if (leader) {
        const yRow = h / 2 - SPACING.sm - Math.ceil(FONT.size.xs / 2);
        const marker = this.add.container(0, yRow);
        const label = this.add
          .text(0, 0, `Gym · ${leader.name}`, {
            fontFamily: FONT.family,
            fontSize: `${FONT.size.xs}px`,
            color: hexCss(COLORS.warm),
            fontStyle: 'bold',
          })
          .setOrigin(0, 0.5);
        const badge = this.add.graphics();
        const br = 5;
        badge.fillStyle(COLORS.scrim, 0.25);
        badge.fillCircle(0, 1, br);
        badge.fillStyle(COLORS.warm, 1);
        badge.fillCircle(0, 0, br);
        badge.lineStyle(1.5, COLORS.boxBorder, 1);
        badge.strokeCircle(0, 0, br);
        const gap = SPACING.xs;
        const rowW = br * 2 + gap + label.width;
        badge.setPosition(-rowW / 2 + br, 0);
        label.setPosition(-rowW / 2 + br * 2 + gap, 0);
        marker.add([badge, label]);
        container.add(marker);
        makeClickable(marker, Math.max(rowW, 48), FONT.size.xs + SPACING.sm, {
          onClick: () => this.enterGym(leader.gymId),
        });
      }

      // Activate on POINTER_DOWN via makeClickable — the SAME path the rest of the UI
      // uses (ui/clickable.ts) — so even a very fast tap always registers (the old
      // per-object POINTER_UP handler could be missed by a quick down→up). It sets the
      // centered hit rect + the pointer cursor; we deliberately don't call setSize() on
      // the container (that would re-offset the hit area — see clickable.ts). The
      // hover/press scale feedback stays as a separate, purely cosmetic cue.
      makeClickable(container, w, h, { onClick: () => this.setOut(node) });
      container.on(Phaser.Input.Events.POINTER_OVER, () => {
        if (!reduced) container.setScale(1.02);
      });
      container.on(Phaser.Input.Events.POINTER_OUT, () => container.setScale(1));
      container.on(Phaser.Input.Events.POINTER_DOWN, () => {
        if (!reduced) container.setScale(0.98);
      });
      container.on(Phaser.Input.Events.POINTER_UP, () => container.setScale(1));

      const mapNode: MapNode = {
        node,
        biome,
        container,
        card,
        width: w,
        height: h,
        ...(leader ? { gymId: leader.gymId } : {}),
      };
      this.nodes.push(mapNode);
      this.drawCard(mapNode);
    }
  }

  private drawCard(n: MapNode): void {
    const { card, width: w, height: h, biome } = n;
    card.clear();
    // A soft tinted card per biome so the map reads at a glance, never harsh.
    card.fillStyle(COLORS.surface, 1);
    card.fillRoundedRect(-w / 2, -h / 2, w, h, RADIUS.md);
    card.lineStyle(1.5, biome.accent, 0.9);
    card.strokeRoundedRect(-w / 2, -h / 2, w, h, RADIUS.md);
    // A gentle color swatch strip at the top to hint the biome's mood.
    card.fillStyle(biome.ground, 0.85);
    card.fillRoundedRect(-w / 2 + SPACING.sm, -h / 2 + 2, w - SPACING.sm * 2, 6, RADIUS.sm);
  }

  // ── navigation ─────────────────────────────────────────────────────────────

  private async setOut(node: RegionNodeDef): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    const session = peekSession(this);
    // Fast-travel is a CLEAN warp: clear any saved walkable-overworld position FIRST so the
    // route spawns at its own default entry (not wherever we last stood there). Walking uses
    // the seam layer's entryEdge/saved-pos instead; the map is the "hop anywhere" shortcut.
    try {
      session?.saveOverworldPos(null);
    } catch {
      /* best-effort; a hiccup must never block the warp */
    }
    // Record the intent (persists today's expedition); never throws into the scene.
    try {
      await session?.setOut();
    } catch {
      /* persistence is best-effort; a hiccup must never block setting out */
    }
    if (!this.scene.isActive()) return;
    goTo(this, 'Route', { data: { nodeId: node.id, biomeId: node.biomeId } });
  }

  private goHome(): void {
    if (this.busy) return;
    this.busy = true;
    goTo(this, 'Town');
  }

  /** Enter the gym anteroom for this biome's Badge Circuit leader. Always reachable. */
  private enterGym(gymId: string): void {
    if (this.busy) return;
    this.busy = true;
    goTo(this, 'Gym', { data: { gymId } });
  }

  /** Enter the League Summit. Only reachable once the Summit is visible (≥ 8 badges). */
  private enterLeague(): void {
    if (this.busy) return;
    this.busy = true;
    goTo(this, 'League', { data: {} });
  }

  // ── layout + placeholder art ───────────────────────────────────────────────

  private relayout(): void {
    const safe = this.layout.safe;
    const cx = this.layout.centerX;
    const top = safe.y + SPACING.lg;

    this.drawGround();

    this.heading?.setPosition(cx, top);
    this.subheading?.setPosition(cx, top + FONT.size.lg + SPACING.sm);

    const nodesTop = top + FONT.size.lg + SPACING.sm + FONT.size.sm + SPACING.xl;

    // Reserve room for the home button at the bottom; flow the cards above it.
    const homeH = this.homeButton?.buttonHeight ?? 48;
    const homeY = safe.y + safe.height - homeH / 2 - SPACING.md;
    this.homeButton?.setPosition(cx, homeY);

    // The Summit invitation (when present) sits just above the "Back to town" button.
    let stackTop = homeY - homeH / 2;
    if (this.summitButton) {
      const summitH = this.summitButton.buttonHeight;
      const summitY = stackTop - SPACING.sm - summitH / 2;
      this.summitButton.setPosition(cx, summitY);
      stackTop = summitY - summitH / 2;
    }

    const areaBottom = stackTop - SPACING.md;
    this.layoutNodes(safe.x, nodesTop, safe.width, areaBottom - nodesTop);
    // Decorative connective paths only make sense for the directly-placed grid; the
    // scrolling phone grid (cards reparented into the list) clears them in layoutNodes.
    if (!this.scroll) this.drawPaths();
    // Re-seat the keyboard focus ring on the (moved) focused card after a relayout.
    if (this.cursor >= 0) this.setCursor(this.cursor);
  }

  /**
   * Place the destination cards in a responsive, BALANCED grid. When the grid is taller
   * than the available area (a tall narrow phone, where one column of 8 cards overflows),
   * the cards live in a touch-drag ScrollList so EVERY biome stays reachable — otherwise
   * the lower cards would fall below the home button with no way to scroll to them. When
   * it fits, the cards are placed directly in the scene (centered) and the decorative
   * connective paths are drawn.
   */
  private layoutNodes(areaX: number, areaY: number, areaW: number, areaH: number): void {
    const count = this.nodes.length;
    if (count === 0) return;
    const cardW = this.nodes[0]!.width;
    const cardH = this.nodes[0]!.height;
    const gapX = SPACING.lg;
    const gapY = SPACING.lg;

    // How many columns physically fit (the responsive ceiling — fewer on a phone).
    const fit = Math.max(1, Math.floor((areaW + gapX) / (cardW + gapX)));
    // Pick a column count that splits the cards as EVENLY as possible (8 -> 4x2, not
    // 6+2): the smallest cols whose row-count matches the fewest-rows that fit. This
    // avoids a lopsided last row sitting under a long top row.
    const minRows = Math.ceil(count / Math.min(fit, count));
    const cols = Math.min(fit, Math.ceil(count / minRows));
    const rows = Math.ceil(count / cols);
    this.gridCols = Math.max(1, cols);

    const gridH = rows * cardH + (rows - 1) * gapY;
    const areaCx = areaX + areaW / 2;
    const rowWidthFor = (rowStart: number): number => {
      const rowCount = Math.min(cols, count - rowStart);
      return rowCount * cardW + (rowCount - 1) * gapX;
    };

    // Does the whole grid fit the area? If not, it must scroll.
    const overflows = gridH > areaH;

    this.scrollViewH = overflows ? areaH : 0;

    if (overflows) {
      // SCROLLING grid (phone): the cards live in the list's content, laid out top-down
      // (content-local y=0 maps to the viewport top). Drop the decorative paths — they're
      // drawn at world coords and would be wrong inside the scrolled content.
      this.paths?.clear();
      const list = this.ensureScroll(areaCx, areaY, areaW, areaH);
      const topPad = SPACING.sm;
      this.nodes.forEach((n, i) => {
        const row = Math.floor(i / cols);
        const col = i % cols;
        const rowStart = row * cols;
        const rowW = rowWidthFor(rowStart);
        const rowX = -rowW / 2 + cardW / 2; // content is centered on the list's cx
        if (n.container.parentContainer !== list.content) {
          list.content.add(n.container);
        }
        n.container.setPosition(
          rowX + col * (cardW + gapX),
          topPad + cardH / 2 + row * (cardH + gapY),
        );
      });
      list.setContentHeight(topPad + gridH + SPACING.sm);
      list.scrollTo(0);
      return;
    }

    // DIRECT grid (roomy screen): tear down any prior list, place cards in the scene,
    // centered vertically within the area, and draw the connective paths.
    if (this.scroll) {
      for (const n of this.nodes) {
        if (n.container.parentContainer === this.scroll.content) {
          this.scroll.content.remove(n.container);
          this.add.existing(n.container);
        }
      }
      this.scroll.destroy();
      this.scroll = undefined;
    }
    const startY = areaY + Math.max(0, (areaH - gridH) / 2) + cardH / 2;
    this.nodes.forEach((n, i) => {
      const row = Math.floor(i / cols);
      const col = i % cols;
      const rowStart = row * cols;
      const rowW = rowWidthFor(rowStart);
      const rowX = areaCx - rowW / 2 + cardW / 2;
      n.container.setPosition(rowX + col * (cardW + gapX), startY + row * (cardH + gapY));
    });
  }

  /** Lazily create (or re-fit) the scrolling viewport for the destination cards. */
  private ensureScroll(cx: number, top: number, w: number, h: number): ScrollList {
    if (!this.scroll) {
      this.scroll = new ScrollList(this, cx, top + h / 2, { width: w, height: h });
    } else {
      this.scroll.setPosition(cx, top + h / 2);
      this.scroll.resize(w, h);
    }
    return this.scroll;
  }

  /** Soft layered ground: a calm gradient-ish backdrop drawn in bands. */
  private drawGround(): void {
    if (!this.ground) return;
    const safe = this.layout.safe;
    const g = this.ground;
    g.clear();
    // A couple of broad, low-contrast bands so the map has gentle depth.
    g.fillStyle(0x202a40, 1);
    g.fillRect(safe.x, safe.y, safe.width, safe.height);
    g.fillStyle(0x24304a, 0.6);
    g.fillRoundedRect(
      safe.x + SPACING.md,
      safe.y + safe.height * 0.45,
      safe.width - SPACING.md * 2,
      safe.height * 0.5,
      RADIUS.lg,
    );
  }

  /** Faint connective paths between the destination cards (decorative, calm). */
  private drawPaths(): void {
    if (!this.paths) return;
    const p = this.paths;
    p.clear();
    if (this.nodes.length < 2) return;
    p.lineStyle(2, COLORS.border, 0.5);
    for (let i = 0; i < this.nodes.length - 1; i++) {
      const a = this.nodes[i]!.container;
      const b = this.nodes[i + 1]!.container;
      p.lineBetween(a.x, a.y, b.x, b.y);
    }
  }
}

/** A 0xRRGGBB number -> '#rrggbb' for Phaser Text color props. */
function hexCss(hex: number): string {
  return `#${(hex & 0xffffff).toString(16).padStart(6, '0')}`;
}

/** Capitalize a short biome-kind word (e.g. 'meadow' -> 'Meadow') for the card sub-line. */
function titleCase(s: string): string {
  return s.length === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1);
}
